import Navigation from "@/components/Navigation";
import TopHeader from "@/components/TopHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Shield, Lock, Eye, Database, UserCheck, Globe, Mail } from "lucide-react";

const Privacy = () => {
  return (
    <div className="min-h-screen bg-cream pb-24">
      <Navigation />
      <TopHeader title="Privacy Policy" />
      
      <main className="px-6 py-8 max-w-3xl mx-auto space-y-6">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-serif text-sage-darker mb-2">Privacy Policy</h1>
          <p className="text-muted-foreground">Last updated: December 2024</p>
        </div>

        {/* Our Commitment */}
        <Card className="bg-white border-sage/20">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-sage/20 flex items-center justify-center">
                <Shield className="w-5 h-5 text-sage-dark" />
              </div>
              <h2 className="text-xl font-serif text-sage-darker">Our Commitment to Privacy</h2>
            </div>
            <p className="text-muted-foreground text-sm">
              At Wombs, we understand the sensitive nature of maternal wellness. We are committed to protecting your personal information and ensuring your data is handled with the utmost care and security. Your privacy and trust are our top priorities.
            </p>
          </CardContent>
        </Card>

        {/* Data We Collect */}
        <Card className="bg-white border-sage/20">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                <Database className="w-5 h-5 text-blue-600" />
              </div>
              <h2 className="text-xl font-serif text-sage-darker">Information We Collect</h2>
            </div>
            <div className="space-y-4 text-sm">
              <div>
                <h3 className="font-semibold text-sage-darker mb-2">Account Information</h3>
                <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                  <li>Name and email address (via Google Sign-In)</li>
                  <li>Profile photo</li>
                  <li>Location (if provided)</li>
                  <li>Motherhood phase and interests</li>
                </ul>
              </div>
              <div>
                <h3 className="font-semibold text-sage-darker mb-2">Usage Information</h3>
                <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                  <li>Event registrations and session bookings</li>
                  <li>Community interactions (posts, comments, connections)</li>
                  <li>App usage patterns and preferences</li>
                </ul>
              </div>
              <div>
                <h3 className="font-semibold text-sage-darker mb-2">Payment Information</h3>
                <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                  <li>Payment transactions are processed securely by Razorpay</li>
                  <li>We do not store your credit card or bank details</li>
                  <li>We retain transaction records for accounting purposes</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* How We Use Data */}
        <Card className="bg-white border-sage/20">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                <Eye className="w-5 h-5 text-purple-600" />
              </div>
              <h2 className="text-xl font-serif text-sage-darker">How We Use Your Information</h2>
            </div>
            <ul className="list-disc list-inside space-y-2 text-muted-foreground text-sm">
              <li>To provide and personalize our services</li>
              <li>To match you with other community members based on interests</li>
              <li>To send important updates about events and sessions you have registered for</li>
              <li>To process payments and send confirmations</li>
              <li>To improve our platform and user experience</li>
              <li>To ensure community safety and enforce our terms</li>
            </ul>
          </CardContent>
        </Card>

        {/* Data Protection */}
        <Card className="bg-white border-sage/20">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                <Lock className="w-5 h-5 text-green-600" />
              </div>
              <h2 className="text-xl font-serif text-sage-darker">Data Security</h2>
            </div>
            <div className="space-y-3 text-sm">
              <p className="text-muted-foreground">
                We implement industry-standard security measures to protect your data:
              </p>
              <ul className="list-disc list-inside space-y-2 text-muted-foreground">
                <li>Encrypted data transmission (HTTPS/TLS)</li>
                <li>Secure authentication via Google OAuth</li>
                <li>Regular security audits and monitoring</li>
                <li>Access controls and data minimization practices</li>
                <li>Secure payment processing through Razorpay (PCI-DSS compliant)</li>
              </ul>
            </div>
          </CardContent>
        </Card>

        {/* User Privacy Features */}
        <Card className="bg-white border-sage/20">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                <UserCheck className="w-5 h-5 text-amber-600" />
              </div>
              <h2 className="text-xl font-serif text-sage-darker">Your Privacy Controls</h2>
            </div>
            <ul className="list-disc list-inside space-y-2 text-muted-foreground text-sm">
              <li><strong>Anonymous Posting:</strong> You can post anonymously in circles - your identity is hidden from other users but visible to admins for moderation purposes</li>
              <li><strong>Contact Privacy:</strong> Your email and contact details are never shown to other users</li>
              <li><strong>Profile Control:</strong> You choose what information to share on your profile</li>
              <li><strong>Connection Consent:</strong> Users can only message you after you accept their connection request</li>
              <li><strong>Data Access:</strong> You can request a copy of your data at any time</li>
              <li><strong>Account Deletion:</strong> You can request deletion of your account and associated data</li>
            </ul>
          </CardContent>
        </Card>

        {/* Data Sharing */}
        <Card className="bg-white border-sage/20">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                <Globe className="w-5 h-5 text-red-600" />
              </div>
              <h2 className="text-xl font-serif text-sage-darker">Data Sharing</h2>
            </div>
            <div className="space-y-3 text-sm">
              <p className="text-muted-foreground font-medium">We do NOT sell your personal data to third parties.</p>
              <p className="text-muted-foreground">We may share limited data with:</p>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                <li>Payment processors (Razorpay) for transaction processing</li>
                <li>Email service providers for transactional emails</li>
                <li>Law enforcement when legally required</li>
              </ul>
            </div>
          </CardContent>
        </Card>

        {/* Contact */}
        <Card className="bg-white border-sage/20">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-sage/20 flex items-center justify-center">
                <Mail className="w-5 h-5 text-sage-dark" />
              </div>
              <h2 className="text-xl font-serif text-sage-darker">Contact Us</h2>
            </div>
            <p className="text-muted-foreground text-sm">
              For privacy-related questions, data requests, or concerns, please contact our Privacy Team at{" "}
              <a href="mailto:privacy@wombs.com" className="text-sage-dark underline">privacy@wombs.com</a>
            </p>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center pt-6 border-t border-sage/20">
          <p className="text-muted-foreground text-sm">
            By using Wombs, you agree to this Privacy Policy and our{" "}
            <a href="/terms" className="text-sage-dark underline">Terms of Service</a>.
          </p>
        </div>
      </main>

      <Navigation />
    </div>
  );
};

export default Privacy;
